import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "katex/dist/katex.min.css";
import { InlineMath, BlockMath } from "react-katex";
import { getApiUrl } from "../config";
import { evaluate } from "mathjs";
import Fraction from "fraction.js";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Tooltip,
  Chip,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import CalculateIcon from "@mui/icons-material/Calculate";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SendIcon from "@mui/icons-material/Send";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HistoryIcon from "@mui/icons-material/History";
import SchoolIcon from "@mui/icons-material/School";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { toast } from "react-toastify";

// ── keyframes ──
const fadeUp = keyframes`from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}`;
const slideIn = keyframes`from{opacity:0;transform:translateX(-100%)}to{opacity:1;transform:translateX(0)}`;
const urgPulse = keyframes`0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,.3)}50%{box-shadow:0 0 0 10px rgba(239,68,68,0)}`;
const optSlide = keyframes`from{opacity:0;transform:translateX(-8px)}to{opacity:1;transform:translateX(0)}`;
const scoreReveal = keyframes`from{opacity:0;transform:scale(.85)}to{opacity:1;transform:scale(1)}`;
const pulse = keyframes`0%,100%{opacity:1}50%{opacity:.55}`;

// ── styled ──
const Root = styled(Box)({
  minHeight: "100vh",
  background: "#f0f3f9",
  display: "flex",
  flexDirection: "column",
  fontFamily: "'Sora',sans-serif",
  overflow: "hidden",
});

const TopBar = styled(Box)({
  height: 62,
  background: "#0c0a00",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 22px",
  position: "sticky",
  top: 0,
  zIndex: 200,
  flexShrink: 0,
  borderBottom: "1px solid rgba(255,255,255,.06)",
  boxShadow: "0 4px 24px rgba(0,0,0,.28)",
});

const TimerChip = styled(Box)(({ urgent, warning }) => ({
  display: "flex",
  alignItems: "center",
  gap: 7,
  background:
    urgent === "true"
      ? "rgba(239,68,68,.15)"
      : warning === "true"
      ? "rgba(245,158,11,.12)"
      : "rgba(255,255,255,.08)",
  border: `1.5px solid ${
    urgent === "true"
      ? "rgba(239,68,68,.5)"
      : warning === "true"
      ? "rgba(245,158,11,.4)"
      : "rgba(255,255,255,.1)"
  }`,
  borderRadius: 10,
  padding: "5px 14px",
  backdropFilter: "blur(8px)",
  animation:
    urgent === "true" ? `${urgPulse} 1.4s ease-in-out infinite` : "none",
}));

const CalcSide = styled(Box)(({ open }) => ({
  width: open === "true" ? 288 : 0,
  minWidth: open === "true" ? 288 : 0,
  background: "#0a0800",
  borderRight: "1px solid rgba(255,255,255,.06)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  transition:
    "width .32s cubic-bezier(.22,1,.36,1), min-width .32s cubic-bezier(.22,1,.36,1)",
  flexShrink: 0,
}));

const CalcInner = styled(Box)({
  width: 288,
  display: "flex",
  flexDirection: "column",
  height: "100%",
  padding: "18px 14px",
  overflowY: "auto",
});

const CalcScreen = styled(Box)({
  background: "#060400",
  borderRadius: 12,
  padding: "12px 14px",
  marginBottom: 12,
  minHeight: 76,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  justifyContent: "flex-end",
  border: "1px solid rgba(245,158,11,.15)",
});

const CBtn = styled(Button)(({ cv }) => ({
  borderRadius: 9,
  minWidth: 0,
  padding: "9px 4px",
  fontFamily: "'Sora',sans-serif",
  fontWeight: 700,
  fontSize: ".82rem",
  textTransform: "none",
  transition: "all .16s ease",
  ...(cv === "op" && {
    background: "#2d1a00",
    color: "#fbbf24",
    "&:hover": { background: "#451f00", transform: "translateY(-1px)" },
  }),
  ...(cv === "fn" && {
    background: "#1a1000",
    color: "#fb923c",
    fontSize: ".72rem",
    "&:hover": { background: "#2d1a00", transform: "translateY(-1px)" },
  }),
  ...(cv === "eq" && {
    background: "linear-gradient(135deg,#b45309,#f59e0b)",
    color: "#fff",
    gridColumn: "span 4",
    boxShadow: "0 6px 16px -4px rgba(180,83,9,.5)",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 10px 22px -4px rgba(180,83,9,.6)",
    },
  }),
  ...(cv === "cl" && {
    background: "#450a0a",
    color: "#fca5a5",
    "&:hover": { background: "#7f1d1d", transform: "translateY(-1px)" },
  }),
  ...(cv === "num" && {
    background: "#1a1200",
    color: "#e2e8f0",
    "&:hover": { background: "#261a00", transform: "translateY(-1px)" },
  }),
  ...(cv === "mod" && {
    background: "transparent",
    color: "#64748b",
    border: "1px solid rgba(255,255,255,.1)",
    fontSize: ".7rem",
    "&:hover": { background: "rgba(255,255,255,.05)" },
  }),
}));

const QCard = styled(Box)({
  background: "#fff",
  borderRadius: 20,
  border: "1.5px solid #e8edf5",
  padding: "32px 36px 26px",
  boxShadow: "0 4px 28px -8px rgba(15,23,42,.09)",
  animation: `${fadeUp} .32s cubic-bezier(.22,1,.36,1) both`,
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
});

const OptRow = styled(Box)(({ sel, correct, wrong }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: 13,
  padding: "13px 16px",
  borderRadius: 12,
  border: correct
    ? "2px solid #22c55e"
    : wrong
    ? "2px solid #ef4444"
    : sel
    ? "2px solid #f59e0b"
    : "2px solid #e8edf5",
  background: correct
    ? "#f0fdf4"
    : wrong
    ? "#fef2f2"
    : sel
    ? "#fffbeb"
    : "#fafbff",
  cursor: correct || wrong ? "default" : "pointer",
  marginBottom: 9,
  transition: "all .2s ease",
  animation: `${optSlide} .28s cubic-bezier(.22,1,.36,1) both`,
  "&:hover":
    !correct && !wrong
      ? {
          borderColor: sel ? "#d97706" : "#fcd34d",
          background: sel ? "#fef3c7" : "#fffdf0",
          transform: "translateX(4px)",
        }
      : {},
}));

const OptCircle = styled(Box)(({ sel, correct, wrong }) => ({
  width: 34,
  height: 34,
  borderRadius: "50%",
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 800,
  fontSize: ".85rem",
  background: correct
    ? "linear-gradient(135deg,#16a34a,#22c55e)"
    : wrong
    ? "linear-gradient(135deg,#dc2626,#ef4444)"
    : sel
    ? "linear-gradient(135deg,#b45309,#f59e0b)"
    : "#f1f5f9",
  color: correct || wrong || sel ? "#fff" : "#64748b",
  boxShadow: correct
    ? "0 4px 14px rgba(22,163,74,.35)"
    : wrong
    ? "0 4px 14px rgba(220,38,38,.35)"
    : sel
    ? "0 4px 14px rgba(180,83,9,.35)"
    : "none",
  transition: "all .2s ease",
}));

const NavDot = styled(Box)(({ answered, current }) => ({
  width: 36,
  height: 36,
  borderRadius: 9,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "'Sora',sans-serif",
  fontWeight: 700,
  fontSize: ".78rem",
  cursor: "pointer",
  transition: "all .18s ease",
  flexShrink: 0,
  border: current
    ? "2px solid #f59e0b"
    : answered
    ? "2px solid #22c55e"
    : "2px solid #e2e8f0",
  background: current ? "#fef3c7" : answered ? "#f0fdf4" : "#fff",
  color: current ? "#b45309" : answered ? "#15803d" : "#64748b",
  "&:hover": { transform: "scale(1.1)", borderColor: "#fcd34d" },
}));

// ── helpers ──
const toFrac = (n) => {
  try {
    return new Fraction(n).toFraction(true);
  } catch {
    return String(n);
  }
};

const renderContent = (text) => {
  if (!text) return null;
  return text
    .split(/(\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|\\\([\s\S]+?\\\)|\$[^$]+\$)/g)
    .map((p, i) => {
      if (p.startsWith("$$") && p.endsWith("$$"))
        return (
          <div key={i} style={{ margin: "8px 0" }}>
            <BlockMath math={p.slice(2, -2)} />
          </div>
        );
      if (p.startsWith("\\[") && p.endsWith("\\]"))
        return (
          <div key={i} style={{ margin: "8px 0" }}>
            <BlockMath math={p.slice(2, -2)} />
          </div>
        );
      if (p.startsWith("\\(") && p.endsWith("\\)"))
        return <InlineMath key={i} math={p.slice(2, -2)} />;
      if (p.startsWith("$") && p.endsWith("$"))
        return <InlineMath key={i} math={p.slice(1, -1)} />;
      return (
        <span key={i} style={{ whiteSpace: "pre-line" }}>
          {p}
        </span>
      );
    });
};

// ── component ──
const JambExamPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = JSON.parse(localStorage.getItem("authToken"));

  const subjectId = useMemo(
    () => new URLSearchParams(location.search).get("subjectId"),
    [location.search]
  );

  const [questions, setQuestions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [timeLeft, setTimeLeft] = useState(50 * 60);
  const [examYear, setExamYear] = useState(null);
  const [score, setScore] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCorrections, setShowCorrections] = useState(false);
  const [corrections, setCorrections] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const [disp, setDisp] = useState("");
  const [expr, setExpr] = useState("");
  const [calcOpen, setCalcOpen] = useState(false);
  const [ans, setAns] = useState(0);
  const [hist, setHist] = useState([]);
  const [showHist, setShowHist] = useState(false);
  const [angMode, setAngMode] = useState("DEG");
  const [idx, setIdx] = useState(0);

  const submitRef = useRef();
  const answeredCount = Object.keys(selectedOptions).length;
  const isUrgent = timeLeft <= 60;
  const isWarn = timeLeft <= 300 && !isUrgent;

  useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&family=DM+Serif+Display:ital@0;1&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    if (!token?.access) {
      navigate("/student-login");
      return;
    }
    (async () => {
      setLoadingQuestions(true);
      try {
        const API_URL = getApiUrl();
        const res = await fetch(
          `${API_URL}/desktop-exam-questions/${subjectId}/`,
          {
            headers: { Authorization: `Bearer ${token.access}` },
          }
        );
        if (res.status === 403) {
          toast.error("No active plan. Please subscribe.");
          navigate("/subjects-screen");
          return;
        }
        const data = await res.json();
        if (data.objective_time) {
          const [h, m, s] = data.objective_time.split(":").map(Number);
          setTimeLeft(h * 3600 + m * 60 + s);
        }
        setExamYear(data.year || null);
        setQuestions(data.questions || []);
      } catch {
        toast.error("Failed to load questions.");
        navigate("/subjects-screen");
      } finally {
        setLoadingQuestions(false);
      }
    })();
  }, [navigate, subjectId, token?.access]);

  const handleSubmitCore = useCallback(async () => {
    if (!token?.access) {
      toast.error("No auth token");
      return;
    }

    if (submitting) return;

    setSubmitting(true);

    try {
      const API_URL = getApiUrl();

      const res = await fetch(
        `${API_URL}/desktop-exam-submit/${subjectId}/submit/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token.access}`,
          },
          body: JSON.stringify({
            answers: selectedOptions,
          }),
        }
      );

      const data = await res.json();

      setScore({
        correct: data.correct_count,
        total: data.total_questions,
      });

      setCorrections(data.results || []);
      setShowModal(true);
    } catch (error) {
      console.error(error);
      toast.error("Failed to submit exam");
    } finally {
      setSubmitting(false);
    }
  }, [selectedOptions, subjectId, token?.access, submitting]);

  useEffect(() => {
    submitRef.current = handleSubmitCore;
  });

  useEffect(() => {
    if (loadingQuestions || questions.length === 0) return;
    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          submitRef.current?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [questions, loadingQuestions]);

  const formatTime = () => {
    const h = Math.floor(timeLeft / 3600);
    const m = Math.floor((timeLeft % 3600) / 60);
    const s = timeLeft % 60;
    if (h > 0)
      return `${String(h).padStart(2, "0")}:${String(m).padStart(
        2,
        "0"
      )}:${String(s).padStart(2, "0")}`;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  const correctionMap = useMemo(() => {
    if (!Array.isArray(corrections)) return {};
    return Object.fromEntries(corrections.map((c) => [c.question_id, c]));
  }, [corrections]);

  const append = (v) => setDisp((p) => p + v);
  const clearD = () => {
    setDisp("");
    setExpr("");
  };
  const delLast = () => setDisp((p) => p.slice(0, -1));

  const calc = useCallback(() => {
    try {
      let e = disp.replace(/ANS/g, ans);
      if (angMode === "DEG")
        e = e
          .replace(/sin\(([^)]*)\)/g, "sin(($1)*pi/180)")
          .replace(/cos\(([^)]*)\)/g, "cos(($1)*pi/180)")
          .replace(/tan\(([^)]*)\)/g, "tan(($1)*pi/180)");
      const res = evaluate(e);
      const frac = toFrac(res);
      setAns(res);
      setExpr(disp + " =");
      setDisp(frac);
      setHist((p) => [...p, `${disp} = ${frac}`]);
    } catch {
      setDisp("Error");
    }
  }, [disp, ans, angMode]);

  useEffect(() => {
    const hk = (e) => {
      if (!calcOpen) return;
      if ("0123456789+-*/().".includes(e.key)) append(e.key);
      if (e.key === "Enter") calc();
      if (e.key === "Backspace") delLast();
    };
    window.addEventListener("keydown", hk);
    return () => window.removeEventListener("keydown", hk);
  }, [calc, calcOpen]);

  const calcRows = [
    [
      { l: "sin", a: () => append("sin("), v: "fn" },
      { l: "cos", a: () => append("cos("), v: "fn" },
      { l: "tan", a: () => append("tan("), v: "fn" },
      { l: "log", a: () => append("log("), v: "fn" },
    ],
    [
      { l: "√", a: () => append("sqrt("), v: "fn" },
      { l: "x²", a: () => append("^2"), v: "fn" },
      { l: "xʸ", a: () => append("^"), v: "fn" },
      { l: "π", a: () => append("pi"), v: "fn" },
    ],
    [
      { l: "ANS", a: () => append("ANS"), v: "fn" },
      { l: "( )", a: () => append("("), v: "fn" },
      { l: "e", a: () => append("e"), v: "fn" },
      { l: "x!", a: () => append("!"), v: "fn" },
    ],
    [
      { l: "7", a: () => append("7"), v: "num" },
      { l: "8", a: () => append("8"), v: "num" },
      { l: "9", a: () => append("9"), v: "num" },
      { l: "÷", a: () => append("/"), v: "op" },
    ],
    [
      { l: "4", a: () => append("4"), v: "num" },
      { l: "5", a: () => append("5"), v: "num" },
      { l: "6", a: () => append("6"), v: "num" },
      { l: "×", a: () => append("*"), v: "op" },
    ],
    [
      { l: "1", a: () => append("1"), v: "num" },
      { l: "2", a: () => append("2"), v: "num" },
      { l: "3", a: () => append("3"), v: "num" },
      { l: "−", a: () => append("-"), v: "op" },
    ],
    [
      { l: "0", a: () => append("0"), v: "num" },
      { l: ".", a: () => append("."), v: "num" },
      { l: "⌫", a: delLast, v: "cl" },
      { l: "+", a: () => append("+"), v: "op" },
    ],
  ];

  const currentQ = questions[idx];
  const totalQ = questions.length;
  const scorePercent = score
    ? Math.round((score.correct / score.total) * 100)
    : 0;
  const scoreGradient =
    scorePercent >= 70
      ? "linear-gradient(135deg,#16a34a,#22c55e)"
      : scorePercent >= 50
      ? "linear-gradient(135deg,#b45309,#f59e0b)"
      : "linear-gradient(135deg,#dc2626,#ef4444)";
  const scoreColor =
    scorePercent >= 70 ? "#22c55e" : scorePercent >= 50 ? "#f59e0b" : "#ef4444";

  if (loadingQuestions)
    return (
      <Root
        sx={{
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          background: "#0c0a00",
        }}
      >
        <CircularProgress size={50} thickness={4} sx={{ color: "#f59e0b" }} />
        <Typography
          sx={{
            fontFamily: "'Sora',sans-serif",
            color: "rgba(255,255,255,.5)",
            fontSize: ".92rem",
          }}
        >
          Loading JAMB questions…
        </Typography>
      </Root>
    );

  return (
    <Root>
      {/* TOP BAR */}
      <TopBar>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1.2, mr: 0.5 }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "9px",
                background: "linear-gradient(135deg,#b45309,#f59e0b)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SchoolIcon sx={{ color: "#fff", fontSize: 17 }} />
            </Box>
            <Typography
              sx={{
                fontFamily: "'Sora',sans-serif",
                fontWeight: 700,
                color: "#e2e8f0",
                fontSize: ".92rem",
                display: { xs: "none", md: "block" },
              }}
            >
              LiteUpper
            </Typography>
          </Box>

          <Tooltip
            title={calcOpen ? "Close Calculator" : "Open Calculator"}
            arrow
          >
            <Box
              onClick={() => setCalcOpen((o) => !o)}
              sx={{
                width: 38,
                height: 38,
                borderRadius: "10px",
                background: calcOpen
                  ? "linear-gradient(135deg,#b45309,#f59e0b)"
                  : "rgba(255,255,255,.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all .22s ease",
                boxShadow: calcOpen
                  ? "0 4px 14px -4px rgba(180,83,9,.5)"
                  : "none",
                "&:hover": {
                  background: calcOpen
                    ? "linear-gradient(135deg,#92400e,#d97706)"
                    : "rgba(255,255,255,.14)",
                },
              }}
            >
              <CalculateIcon
                sx={{
                  fontSize: 20,
                  color: calcOpen ? "#fff" : "rgba(255,255,255,.65)",
                }}
              />
            </Box>
          </Tooltip>

          <Box
            sx={{
              display: { xs: "none", sm: "flex" },
              alignItems: "center",
              gap: 1,
              background: "rgba(245,158,11,.1)",
              border: "1px solid rgba(245,158,11,.22)",
              borderRadius: 100,
              px: 1.5,
              py: 0.5,
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "#fbbf24",
                boxShadow: "0 0 6px #fbbf24",
                animation: `${pulse} 2s ease-in-out infinite`,
              }}
            />
            <Typography
              sx={{
                fontFamily: "'Sora',sans-serif",
                color: "#fbbf24",
                fontSize: ".7rem",
                fontWeight: 700,
                letterSpacing: ".08em",
              }}
            >
              JAMB {examYear ? `· ${examYear}` : "· OBJECTIVE"}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Sora',sans-serif",
              fontWeight: 700,
              color: "#e2e8f0",
              fontSize: ".88rem",
              lineHeight: 1.2,
            }}
          >
            {showCorrections ? "Corrections" : "Objective Exam"}
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Sora',sans-serif",
              fontSize: ".68rem",
              color: "rgba(255,255,255,.4)",
            }}
          >
            {answeredCount}/{totalQ} answered
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TimerChip
            urgent={isUrgent ? "true" : "false"}
            warning={isWarn ? "true" : "false"}
          >
            <AccessTimeIcon
              sx={{
                fontSize: 16,
                color: isUrgent ? "#f87171" : isWarn ? "#fbbf24" : "#fcd34d",
              }}
            />
            <Typography
              sx={{
                fontFamily: "'Sora',sans-serif",
                fontWeight: 800,
                fontSize: ".95rem",
                color: isUrgent ? "#f87171" : isWarn ? "#fbbf24" : "#e2e8f0",
                letterSpacing: ".04em",
              }}
            >
              {formatTime()}
            </Typography>
          </TimerChip>

          {!showCorrections && (
            <Button
              onClick={handleSubmitCore}
              disabled={submitting}
              endIcon={
                submitting ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <SendIcon sx={{ fontSize: ".95rem !important" }} />
                )
              }
              variant="contained"
              sx={{
                borderRadius: 10,
                textTransform: "none",
                fontFamily: "'Sora',sans-serif",
                fontWeight: 700,
                fontSize: ".82rem",
                py: "7px",
                px: 2.2,
                background: "linear-gradient(105deg,#b45309,#f59e0b)",
                boxShadow: "0 6px 16px -4px rgba(180,83,9,.45)",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 10px 22px -4px rgba(180,83,9,.55)",
                },
                transition: "all .22s ease",
                color: "#fff",
              }}
            >
              {submitting ? "" : "Submit"}
            </Button>
          )}
          {showCorrections && (
            <Button
              onClick={() => navigate("/osubjects")}
              variant="contained"
              sx={{
                borderRadius: 10,
                textTransform: "none",
                fontFamily: "'Sora',sans-serif",
                fontWeight: 700,
                fontSize: ".82rem",
                py: "7px",
                px: 2.2,
                background: "linear-gradient(105deg,#1d4ed8,#3b82f6)",
                boxShadow: "0 6px 16px -4px rgba(37,99,235,.45)",
                "&:hover": { transform: "translateY(-2px)" },
                transition: "all .22s ease",
                color: "#fff",
              }}
            >
              Done
            </Button>
          )}
        </Box>
      </TopBar>

      {/* BODY */}
      <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* CALCULATOR */}
        <CalcSide open={calcOpen ? "true" : "false"}>
          {calcOpen && (
            <CalcInner
              sx={{
                animation: `${slideIn} .3s cubic-bezier(.22,1,.36,1) both`,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CalculateIcon sx={{ color: "#fbbf24", fontSize: 17 }} />
                  <Typography
                    sx={{
                      fontFamily: "'Sora',sans-serif",
                      fontWeight: 700,
                      color: "#e2e8f0",
                      fontSize: ".86rem",
                    }}
                  >
                    Calculator
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 0.8 }}>
                  {["DEG", "RAD"].map((m) => (
                    <CBtn
                      key={m}
                      cv="mod"
                      onClick={() => setAngMode(m)}
                      sx={{
                        background: angMode === m ? "#2d1a00" : "transparent",
                        color: angMode === m ? "#fbbf24" : "#475569",
                      }}
                    >
                      {m}
                    </CBtn>
                  ))}
                </Box>
              </Box>

              <CalcScreen>
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    fontSize: ".7rem",
                    color: "#d97706",
                    mb: 0.4,
                    wordBreak: "break-all",
                  }}
                >
                  {expr || "\u00A0"}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    fontWeight: 800,
                    fontSize: disp.length > 12 ? "1.05rem" : "1.55rem",
                    color: disp === "Error" ? "#f87171" : "#fcd34d",
                    wordBreak: "break-all",
                    textAlign: "right",
                  }}
                >
                  {disp || "0"}
                </Typography>
              </CalcScreen>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 0.8,
                  mb: 0.8,
                }}
              >
                <CBtn cv="cl" onClick={clearD} fullWidth>
                  AC
                </CBtn>
                <CBtn cv="fn" onClick={() => append("(")} fullWidth>
                  ( )
                </CBtn>
              </Box>
              {calcRows.map((row, ri) => (
                <Box
                  key={ri}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4,1fr)",
                    gap: "5px",
                    mb: "5px",
                  }}
                >
                  {row.map((b, bi) => (
                    <CBtn key={bi} cv={b.v} onClick={b.a} fullWidth>
                      {b.l}
                    </CBtn>
                  ))}
                </Box>
              ))}
              <Box sx={{ mt: "5px" }}>
                <CBtn
                  cv="eq"
                  onClick={calc}
                  fullWidth
                  sx={{ py: 1.2, fontSize: "1.05rem" }}
                >
                  =
                </CBtn>
              </Box>

              <Box
                onClick={() => setShowHist((h) => !h)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 2,
                  cursor: "pointer",
                  color: "#475569",
                  "&:hover": { color: "#64748b" },
                }}
              >
                <HistoryIcon sx={{ fontSize: 15 }} />
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    fontSize: ".72rem",
                    fontWeight: 600,
                  }}
                >
                  {showHist ? "Hide" : "Show"} History
                </Typography>
              </Box>
              {showHist && hist.length > 0 && (
                <Box
                  sx={{
                    mt: 0.8,
                    background: "#060400",
                    borderRadius: 9,
                    p: "9px 11px",
                    maxHeight: 110,
                    overflowY: "auto",
                  }}
                >
                  {hist
                    .slice(-8)
                    .reverse()
                    .map((h, i) => (
                      <Typography
                        key={i}
                        sx={{
                          fontFamily: "'Sora',sans-serif",
                          fontSize: ".7rem",
                          color: "#d97706",
                          mb: 0.4,
                          borderBottom: "1px solid #1a1000",
                          pb: 0.4,
                        }}
                      >
                        {h}
                      </Typography>
                    ))}
                </Box>
              )}
            </CalcInner>
          )}
        </CalcSide>

        {/* MAIN */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            p: "22px 28px",
          }}
        >
          {/* Progress */}
          <Box sx={{ mb: 2.5 }}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 0.8 }}
            >
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  fontSize: ".72rem",
                  fontWeight: 700,
                  color: "#64748b",
                  textTransform: "uppercase",
                  letterSpacing: ".07em",
                }}
              >
                {showCorrections ? "Corrections" : "Objective"} · Q{idx + 1} of{" "}
                {totalQ}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  fontSize: ".72rem",
                  color: "#94a3b8",
                }}
              >
                {answeredCount}/{totalQ} answered
              </Typography>
            </Box>
            <Box
              sx={{
                height: 3,
                background: "#e2e8f0",
                borderRadius: 100,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${totalQ > 0 ? (answeredCount / totalQ) * 100 : 0}%`,
                  background: "linear-gradient(90deg,#b45309,#f59e0b)",
                  borderRadius: 100,
                  transition: "width .4s ease",
                }}
              />
            </Box>
          </Box>

          {totalQ === 0 ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography sx={{ fontSize: "2.5rem" }}>📭</Typography>
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  fontWeight: 700,
                  color: "#64748b",
                  fontSize: "1rem",
                }}
              >
                No questions available
              </Typography>
            </Box>
          ) : currentQ ? (
            <>
              <QCard key={currentQ.id}>
                {/* Q header */}
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "12px",
                      background: "linear-gradient(135deg,#b45309,#f59e0b)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 6px 16px -4px rgba(180,83,9,.35)",
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "'Sora',sans-serif",
                        fontWeight: 800,
                        color: "#fff",
                        fontSize: ".88rem",
                      }}
                    >
                      {idx + 1}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: "'Sora',sans-serif",
                        fontSize: ".68rem",
                        fontWeight: 700,
                        color: "#94a3b8",
                        textTransform: "uppercase",
                        letterSpacing: ".07em",
                      }}
                    >
                      Multiple Choice
                    </Typography>
                  </Box>
                  {selectedOptions[currentQ.id] && !showCorrections && (
                    <Chip
                      label="Answered ✓"
                      size="small"
                      sx={{
                        background: "#fef3c7",
                        color: "#b45309",
                        border: "1px solid #fde68a",
                        fontFamily: "'Sora',sans-serif",
                        fontWeight: 700,
                        fontSize: ".7rem",
                      }}
                    />
                  )}
                  {showCorrections && correctionMap[currentQ.id] && (
                    <Chip
                      icon={
                        correctionMap[currentQ.id].result === "incorrect" ? (
                          <CancelIcon
                            sx={{
                              fontSize: "14px !important",
                              color: "#ef4444 !important",
                            }}
                          />
                        ) : (
                          <CheckCircleIcon
                            sx={{
                              fontSize: "14px !important",
                              color: "#22c55e !important",
                            }}
                          />
                        )
                      }
                      label={
                        correctionMap[currentQ.id].result === "incorrect"
                          ? "Incorrect"
                          : "Correct"
                      }
                      size="small"
                      sx={{
                        background:
                          correctionMap[currentQ.id].result === "incorrect"
                            ? "#fef2f2"
                            : "#f0fdf4",
                        color:
                          correctionMap[currentQ.id].result === "incorrect"
                            ? "#dc2626"
                            : "#16a34a",
                        border: `1px solid ${
                          correctionMap[currentQ.id].result === "incorrect"
                            ? "#fecaca"
                            : "#bbf7d0"
                        }`,
                        fontFamily: "'Sora',sans-serif",
                        fontWeight: 700,
                        fontSize: ".7rem",
                      }}
                    />
                  )}
                </Box>

                <Box
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    fontSize: "1rem",
                    color: "#0f172a",
                    lineHeight: 1.78,
                    mb: 3,
                  }}
                >
                  {renderContent(currentQ.text)}
                </Box>

                {currentQ.Eq_img && (
                  <Box
                    sx={{
                      mb: 3,
                      borderRadius: 12,
                      overflow: "hidden",
                      border: "1px solid #e2e8f0",
                      maxWidth: 460,
                      cursor: "pointer",
                    }}
                    onClick={() => setPreviewImage(currentQ.Eq_img)}
                  >
                    <img
                      src={currentQ.Eq_img}
                      alt="question"
                      style={{ width: "100%", display: "block" }}
                    />
                  </Box>
                )}

                <Box sx={{ flex: 1 }}>
                  {currentQ.options?.map((opt, oi) => {
                    const correction = correctionMap[currentQ.id];
                    const isUserWrong =
                      showCorrections &&
                      correction?.result === "incorrect" &&
                      correction.selected_options === opt.option_letter;
                    const isCorrect =
                      showCorrections &&
                      correction &&
                      (Array.isArray(correction.correct_options)
                        ? correction.correct_options.includes(opt.option_letter)
                        : correction.correct_options === opt.option_letter);
                    const isSel =
                      !showCorrections &&
                      selectedOptions[currentQ.id] === opt.id;
                    return (
                      <OptRow
                        key={opt.id}
                        sel={isSel}
                        correct={isCorrect}
                        wrong={isUserWrong}
                        onClick={() =>
                          !showCorrections &&
                          setSelectedOptions((p) => ({
                            ...p,
                            [currentQ.id]: opt.id,
                          }))
                        }
                        sx={{ animationDelay: `${oi * 0.06}s` }}
                      >
                        <OptCircle
                          sel={isSel}
                          correct={isCorrect}
                          wrong={isUserWrong}
                        >
                          {opt.option_letter}
                        </OptCircle>
                        <Box
                          sx={{
                            fontFamily: "'Sora',sans-serif",
                            fontSize: ".92rem",
                            color: "#334155",
                            lineHeight: 1.65,
                            pt: "5px",
                            flex: 1,
                          }}
                        >
                          {renderContent(opt.text)}
                          {opt.Eoption_img && (
                            <Box
                              component="img"
                              src={opt.Eoption_img}
                              alt="option"
                              sx={{
                                maxWidth: 200,
                                display: "block",
                                mt: 1,
                                borderRadius: 8,
                                cursor: "pointer",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setPreviewImage(opt.Eoption_img);
                              }}
                            />
                          )}
                        </Box>
                        {isCorrect && (
                          <CheckCircleIcon
                            sx={{
                              color: "#22c55e",
                              fontSize: 18,
                              flexShrink: 0,
                              mt: "6px",
                            }}
                          />
                        )}
                        {isUserWrong && (
                          <CancelIcon
                            sx={{
                              color: "#ef4444",
                              fontSize: 18,
                              flexShrink: 0,
                              mt: "6px",
                            }}
                          />
                        )}
                      </OptRow>
                    );
                  })}
                </Box>

                {showCorrections &&
                  correctionMap[currentQ.id]?.result === "incorrect" && (
                    <Box
                      sx={{
                        mt: 2,
                        background: "#fef2f2",
                        border: "1px solid #fecaca",
                        borderRadius: 12,
                        p: "14px 18px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <CancelIcon sx={{ color: "#ef4444", fontSize: 16 }} />
                        <Typography
                          sx={{
                            fontFamily: "'Sora',sans-serif",
                            fontWeight: 700,
                            fontSize: ".82rem",
                            color: "#dc2626",
                          }}
                        >
                          Your answer was incorrect
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: "'Sora',sans-serif",
                          fontSize: ".82rem",
                          color: "#991b1b",
                          mb: 0.5,
                        }}
                      >
                        Your answer:{" "}
                        <b>
                          {renderContent(
                            correctionMap[currentQ.id].selected_options
                          )}
                        </b>
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "'Sora',sans-serif",
                          fontSize: ".82rem",
                          color: "#166534",
                        }}
                      >
                        Correct answer:{" "}
                        <b>
                          {Array.isArray(
                            correctionMap[currentQ.id].correct_options
                          )
                            ? correctionMap[currentQ.id].correct_options.join(
                                ", "
                              )
                            : correctionMap[currentQ.id].correct_options}
                        </b>
                      </Typography>
                    </Box>
                  )}
              </QCard>

              {/* NAVIGATION */}
              <Box
                sx={{
                  mt: 2.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Button
                    onClick={() => setIdx((i) => i - 1)}
                    disabled={idx === 0}
                    startIcon={<ChevronLeftIcon />}
                    sx={{
                      borderRadius: 10,
                      textTransform: "none",
                      fontFamily: "'Sora',sans-serif",
                      fontWeight: 600,
                      color: "#64748b",
                      border: "1.5px solid #e2e8f0",
                      px: 2.5,
                      py: 1,
                      "&:hover": {
                        background: "#f1f5f9",
                        borderColor: "#cbd5e1",
                      },
                      "&.Mui-disabled": { opacity: 0.35 },
                    }}
                  >
                    Previous
                  </Button>
                  <Typography
                    sx={{
                      fontFamily: "'Sora',sans-serif",
                      fontSize: ".82rem",
                      color: "#94a3b8",
                      fontWeight: 600,
                    }}
                  >
                    {idx + 1} / {totalQ}
                  </Typography>
                  <Button
                    onClick={() => setIdx((i) => i + 1)}
                    disabled={idx === totalQ - 1}
                    endIcon={<ChevronRightIcon />}
                    sx={{
                      borderRadius: 10,
                      textTransform: "none",
                      fontFamily: "'Sora',sans-serif",
                      fontWeight: 700,
                      color: "#fff",
                      background: "linear-gradient(105deg,#b45309,#f59e0b)",
                      px: 2.5,
                      py: 1,
                      boxShadow: "0 6px 16px -4px rgba(180,83,9,.35)",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 10px 22px -4px rgba(180,83,9,.45)",
                      },
                      "&.Mui-disabled": {
                        opacity: 0.35,
                        background: "#e2e8f0",
                        color: "#94a3b8",
                        boxShadow: "none",
                      },
                      transition: "all .22s ease",
                    }}
                  >
                    Next
                  </Button>
                </Box>

                <Box
                  sx={{
                    background: "#fff",
                    borderRadius: 16,
                    border: "1px solid #e8edf5",
                    p: "12px 16px",
                    boxShadow: "0 2px 12px -4px rgba(15,23,42,.06)",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "'Sora',sans-serif",
                      fontSize: ".66rem",
                      fontWeight: 700,
                      color: "#94a3b8",
                      textTransform: "uppercase",
                      letterSpacing: ".09em",
                      mb: 1.2,
                    }}
                  >
                    Jump to question
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.9 }}>
                    {questions.map((q, i) => (
                      <NavDot
                        key={q.id}
                        answered={!!selectedOptions[q.id]}
                        current={i === idx}
                        onClick={() => setIdx(i)}
                      >
                        {i + 1}
                      </NavDot>
                    ))}
                  </Box>
                  <Box sx={{ display: "flex", gap: 2.5, mt: 1.5 }}>
                    {[
                      { color: "#f59e0b", label: "Current" },
                      { color: "#22c55e", label: "Answered" },
                      { color: "#e2e8f0", label: "Unanswered" },
                    ].map((l) => (
                      <Box
                        key={l.label}
                        sx={{ display: "flex", alignItems: "center", gap: 0.7 }}
                      >
                        <Box
                          sx={{
                            width: 9,
                            height: 9,
                            borderRadius: 3,
                            background: l.color,
                          }}
                        />
                        <Typography
                          sx={{
                            fontFamily: "'Sora',sans-serif",
                            fontSize: ".67rem",
                            color: "#94a3b8",
                          }}
                        >
                          {l.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </>
          ) : null}
        </Box>
      </Box>

      {/* SCORE MODAL */}
      {showModal && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            background: "rgba(12,10,0,.78)",
            backdropFilter: "blur(10px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            p: 2,
          }}
        >
          <Box
            sx={{
              background: "#fff",
              borderRadius: "28px",
              overflow: "hidden",
              width: "100%",
              maxWidth: 400,
              boxShadow: "0 48px 96px -24px rgba(15,23,42,.32)",
              animation: `${scoreReveal} .4s cubic-bezier(.22,1,.36,1) both`,
            }}
          >
            <Box
              sx={{
                background: "linear-gradient(135deg,#0c0a00,#1c1000)",
                p: "32px 36px",
                textAlign: "center",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  backgroundImage:
                    "linear-gradient(rgba(251,191,36,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(251,191,36,.04) 1px,transparent 1px)",
                  backgroundSize: "40px 40px",
                  pointerEvents: "none",
                }}
              />
              <Box
                sx={{
                  width: 96,
                  height: 96,
                  borderRadius: "50%",
                  background: scoreGradient,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 16px",
                  boxShadow: `0 16px 40px -8px ${scoreColor}66`,
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    fontWeight: 800,
                    fontSize: "1.55rem",
                    color: "#fff",
                  }}
                >
                  {scorePercent}%
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontFamily: "'DM Serif Display',serif",
                  fontSize: "1.55rem",
                  color: "#fff",
                  mb: 0.4,
                  position: "relative",
                  zIndex: 1,
                }}
              >
                Exam Submitted!
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  color: "rgba(255,255,255,.45)",
                  fontSize: ".82rem",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                JAMB Objective Result
              </Typography>
            </Box>
            <Box sx={{ p: "24px 32px" }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 2,
                  mb: 3,
                }}
              >
                {[
                  {
                    label: "Correct",
                    value: score?.correct,
                    color: "#22c55e",
                    bg: "#f0fdf4",
                    border: "#bbf7d0",
                  },
                  {
                    label: "Total Questions",
                    value: score?.total,
                    color: "#b45309",
                    bg: "#fef3c7",
                    border: "#fde68a",
                  },
                ].map((stat) => (
                  <Box
                    key={stat.label}
                    sx={{
                      background: stat.bg,
                      border: `1px solid ${stat.border}`,
                      borderRadius: 14,
                      p: "16px 20px",
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "'Sora',sans-serif",
                        fontWeight: 800,
                        fontSize: "2rem",
                        color: stat.color,
                        lineHeight: 1,
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "'Sora',sans-serif",
                        fontSize: ".72rem",
                        color: "#64748b",
                        fontWeight: 600,
                        mt: 0.5,
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  onClick={() => navigate(-1)}
                  fullWidth
                  variant="outlined"
                  sx={{
                    borderRadius: "11px",
                    textTransform: "none",
                    fontFamily: "'Sora',sans-serif",
                    fontWeight: 600,
                    py: 1.35,
                    fontSize: ".86rem",
                    color: "#64748b",
                    borderColor: "#e2e8f0",
                    borderWidth: 1.5,
                    "&:hover": {
                      background: "#f8faff",
                      borderColor: "#cbd5e1",
                    },
                  }}
                >
                  Exit
                </Button>
                <Button
                  onClick={() => {
                    setShowModal(false);
                    setShowCorrections(true);
                    setIdx(0);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  fullWidth
                  variant="contained"
                  sx={{
                    borderRadius: "11px",
                    textTransform: "none",
                    fontFamily: "'Sora',sans-serif",
                    fontWeight: 700,
                    py: 1.35,
                    fontSize: ".86rem",
                    background: "linear-gradient(105deg,#b45309,#f59e0b)",
                    boxShadow: "0 10px 24px -6px rgba(180,83,9,.38)",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 14px 30px -6px rgba(180,83,9,.48)",
                    },
                    transition: "all .28s ease",
                  }}
                >
                  View Corrections
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* IMAGE PREVIEW */}
      {previewImage && (
        <Box
          onClick={() => setPreviewImage(null)}
          sx={{
            position: "fixed",
            inset: 0,
            background: "rgba(12,10,0,.88)",
            backdropFilter: "blur(12px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            p: 3,
            cursor: "zoom-out",
          }}
        >
          <Box
            component="img"
            src={previewImage}
            alt="preview"
            sx={{
              maxWidth: "100%",
              maxHeight: "90vh",
              borderRadius: 16,
              boxShadow: "0 32px 80px rgba(0,0,0,.5)",
              animation: `${scoreReveal} .3s cubic-bezier(.22,1,.36,1) both`,
            }}
          />
        </Box>
      )}
    </Root>
  );
};

export default JambExamPage;
